<template>
  <div v-if="props.itemsAmount" class="base-paginator">
    <button
      class="text-body-2 text-black-monochrome"
      :disabled="currentPage === 1"
      @click="prevPage"
    >
      {{ $t('general.prev') }}
    </button>
    <div class="base-paginator-pages">
      <button
        v-for="page in visiblePages"
        :key="page"
        class="page-butt text-body-3 text-black-monochrome"
        :class="{ 'page-butt-active': currentPage === page }"
        :disabled="page === '...'"
        @click="typeof page === 'number' && changePage(page)"
      >
        {{ page }}
      </button>
    </div>

    <button
      class="text-body-2 text-black-monochrome"
      :disabled="currentPage === pagesAmount"
      @click="nextPage"
    >
      {{ $t('general.next') }}
    </button>
  </div>
</template>

<script setup lang="ts">
interface IProps {
  limit: number;
  offset: number;
  itemsAmount: number;
}

interface IEmits {
  (e: 'change', value: number): void;
}

const props = defineProps<IProps>();
const emit = defineEmits<IEmits>();

const currentPage = ref(1);
const pagesAmount = computed(() => Math.ceil(props.itemsAmount / props.limit));
const currentOffset = computed(() => (currentPage.value - 1) * props.limit);

function getPages(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [1];
  const numMiddle = 3;
  let left = current - Math.floor(numMiddle / 2);
  let right = current + Math.floor(numMiddle / 2);

  if (left < 2) {
    left = 2;
    right = left + numMiddle - 1;
  }
  if (right > total - 1) {
    right = total - 1;
    left = right - numMiddle + 1;
  }

  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('...');
  pages.push(total);

  return pages;
}

const visiblePages = computed(() => {
  const totalPages = pagesAmount.value;
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  return getPages(currentPage.value, totalPages);
});

const changePage = (page: number) => {
  if (currentPage.value === page) return;
  currentPage.value = page;

  const newOffset = currentPage.value === 1 ? 0 : currentOffset.value;

  emit('change', newOffset);
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    emit('change', currentOffset.value);
  }
};

const nextPage = () => {
  if (currentPage.value < pagesAmount.value) {
    currentPage.value += 1;
    emit('change', currentOffset.value);
  }
};

const syncCurrentPage = (offset: number) => {
  currentPage.value = offset === 0 ? 1 : Math.floor(offset / props.limit) + 1;
};

// Keep the active page in sync when the parent resets the offset externally
// (e.g. a filter/sort/search change snaps the listing back to the first page).
watch(() => props.offset, syncCurrentPage);

onMounted(() => {
  syncCurrentPage(props.offset);
});
</script>

<style scoped lang="scss">
.base-paginator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 40px;
  button {
    @include drop-button-styles;
  }
  &-pages {
    display: flex;
    align-items: center;
    gap: 8px;
    .page-butt {
      display: flex;
      width: 28px;
      height: 30px;
      padding: 8px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 6px;
      border: 1px solid var(--gray-monochrome-40);
      background: var(--white-monochrome);
      &-active {
        border-color: transparent;
        background: var(--black-monochrome);
        color: var(--white-monochrome);
      }
    }
  }
}

@media (max-width: 768px) {
  .base-paginator {
    justify-content: space-between;
    width: 100%;
    max-width: 386px;
    gap: 8px;
  }
}
</style>
